import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Paper, Typography, Avatar, CircularProgress, Chip, Slide, Alert } from '@mui/material';
import { Send as SendIcon, Person as PersonIcon, Close as CloseIcon, Lightbulb as LightbulbIcon, Warning as WarningIcon } from '@mui/icons-material';
import axios from 'axios';

const Chatbot = ({ isOpen, onClose, onError }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Bonjour ! Je suis votre assistant virtuel ECOTIJ spécialisé en équipements d'éclairage professionnel. Comment puis-je vous accompagner aujourd'hui ?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const messagesEndRef = useRef(null);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  const MAX_ERRORS = 3; // Désactiver après 3 erreurs consécutives

  const suggestions = [
    "Nos produits",
    "Vos horaires",
    "Comment contacter",
    "À propos ECOTIJ"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (text = inputMessage) => {
    if (!text.trim() || isLoading) return;

    // Vérifier si trop d'erreurs
    if (errorCount >= MAX_ERRORS) {
      setConnectionError(true);
      if (onError) onError();
      return;
    }

    setShowSuggestions(false);
    const userMessage = {
      id: messages.length + 1,
      text: text,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/chatbot.php`, 
        {
          question: text,
          history: messages.slice(-6).map(m => ({ role: m.sender, content: m.text })) // Historique optimisé : 6 messages
        },
        {
          timeout: 30000, // Timeout de 30 secondes
          validateStatus: (status) => status < 600, // Accepter même erreurs serveur
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.answer) {
        const botMessage = {
          id: messages.length + 2,
          text: response.data.answer,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        // Réinitialiser le compteur d'erreurs en cas de succès
        setErrorCount(0);
        setConnectionError(false);
      } else {
        throw new Error('Réponse invalide');
      }
    } catch (error) {
      // Protection ultime : même si la gestion d'erreur échoue, le site continue
      try {
        // Logger l'erreur mais continuer à fonctionner
        console.warn('[CHATBOT] Erreur API:', error.message);
        
        // Incrémenter le compteur d'erreurs
        const newErrorCount = errorCount + 1;
        setErrorCount(newErrorCount);

        let errorText = "";
        
        // Si l'API répond avec un fallback message
        if (error.response?.data?.answer && error.response?.data?.fallback) {
          errorText = error.response.data.answer;
        }
        // Sinon, message d'erreur contextuel
        else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          errorText = "Le serveur met du temps à répondre. Vous pouvez nous contacter directement au 06 61 83 77 26.";
        } else if (error.response?.status === 429) {
          errorText = "Trop de requêtes. Veuillez patienter quelques instants avant de réessayer.";
        } else if (error.response?.status >= 500) {
          errorText = "Service temporairement indisponible. Contactez-nous au 06 61 83 77 26 ou contact@ecotij.ma.";
        } else if (!navigator.onLine) {
          errorText = "Pas de connexion internet. Vérifiez votre réseau.";
        } else {
          errorText = "Erreur temporaire. Réessayez ou contactez-nous au 06 61 83 77 26.";
        }

        // Après 3 erreurs consécutives, désactiver le chatbot
        if (newErrorCount >= MAX_ERRORS) {
          errorText = "Le chatbot est temporairement indisponible. Le site reste pleinement fonctionnel. Contactez-nous au 06 61 83 77 26 ou via la page Contact.";
          setConnectionError(true);
        }

        const errorMessage = {
          id: messages.length + 2,
          text: errorText,
          sender: 'bot',
          timestamp: new Date(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);

        // Notifier le parent en cas d'erreurs multiples (masquer le chatbot)
        if (newErrorCount >= MAX_ERRORS && onError) {
          setTimeout(() => onError(), 3000);
        }
      } catch (fatalError) {
        // Capture les erreurs FATALES pour ne JAMAIS crasher le site
        console.error('[CHATBOT FATAL]', fatalError);
        setConnectionError(true);
        if (onError) onError();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <Slide 
      direction="up" 
      in={isOpen} 
      mountOnEnter 
      unmountOnExit 
      timeout={400}
      style={{ position: 'fixed', bottom: 0, right: 0, zIndex: 9998 }}
    >
      <Paper
        elevation={16}
        sx={{
          position: 'fixed',
          bottom: { xs: 0, sm: 16, md: 20 },
          top: 'auto',
          right: { xs: 0, sm: 16, md: 20 },
          left: 'auto',
          width: { xs: '100%', sm: '400px', md: '420px' },
          maxWidth: { xs: '100%', sm: 'calc(100vw - 32px)' },
          height: { xs: 'calc(100vh - 70px)', sm: '80vh', md: '650px' },
          maxHeight: { xs: 'calc(100vh - 70px)', sm: 'calc(100vh - 100px)', md: '650px' },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: '20px 20px 0 0', sm: '20px' },
          overflow: 'hidden',
          zIndex: 9998,
          border: '1px solid rgba(0, 119, 184, 0.15)',
          boxShadow: { xs: '0 -4px 20px rgba(0, 0, 0, 0.15)', sm: '0 10px 40px rgba(0, 0, 0, 0.15)' },
          transition: 'all 0.3s ease',
        }}
      >
        {/* Header professionnel ECOTIJ */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #0077B8 0%, #00ACEA 100%)',
            color: 'white',
            p: { xs: 2, md: 2.5 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: { xs: 40, md: 44 },
                height: { xs: 40, md: 44 },
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <LightbulbIcon sx={{ fontSize: { xs: 24, md: 26 }, color: '#F5A000' }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                Assistant ECOTIJ
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                🟢 En ligne
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              '&:hover': { 
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Zone de messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            p: { xs: 1.5, md: 2 },
            bgcolor: '#f5f7fa',
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '5px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#cbd5e1',
              borderRadius: '10px',
              '&:hover': {
                background: '#94a3b8',
              },
            },
          }}
        >
          {/* Alerte d'erreur critique */}
          {connectionError && (
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ 
                mb: 2,
                borderRadius: '12px',
                '& .MuiAlert-message': {
                  fontSize: '0.85rem'
                }
              }}
            >
              Le chatbot est temporairement indisponible. Le site fonctionne normalement. 
              Contactez-nous via la page Contact pour toute question.
            </Alert>
          )}

          {/* Suggestions rapides */}
          {showSuggestions && messages.length === 1 && !connectionError && (
            <Box sx={{ mb: 2.5 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#64748b', 
                  mb: 1.5, 
                  display: 'block', 
                  fontWeight: 600, 
                  fontSize: { xs: '0.8rem', md: '0.85rem' },
                  letterSpacing: '0.02em'
                }}
              >
                💡 Questions rapides :
              </Typography>
              <Box 
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)' },
                  gap: { xs: 1, md: 1.25 },
                }}
              >
                {suggestions.map((suggestion, index) => (
                  <Chip
                    key={index}
                    label={suggestion}
                    onClick={() => sendMessage(suggestion)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: 'white',
                      border: '1.5px solid #cbd5e1',
                      color: '#334155',
                      fontSize: { xs: '0.8rem', md: '0.85rem' },
                      fontWeight: 500,
                      height: { xs: '36px', md: '38px' },
                      borderRadius: '10px',
                      '& .MuiChip-label': {
                        px: { xs: 1.5, md: 2 },
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        lineHeight: 1.2
                      },
                      '&:hover': {
                        bgcolor: '#0077B8',
                        color: 'white',
                        borderColor: '#0077B8',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0, 119, 184, 0.25)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {messages.map((message, index) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                mb: 2.5,
                gap: 1,
                animation: 'slideIn 0.3s ease',
                '@keyframes slideIn': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
                {message.sender === 'bot' && (
                  <Avatar 
                    sx={{ 
                      bgcolor: '#0077B8',
                      width: 32,
                      height: 32,
                    }}
                  >
                    <LightbulbIcon sx={{ fontSize: 18, color: '#F5A000' }} />
                  </Avatar>
                )}
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: { xs: '85%', md: '78%' },
                    p: { xs: 1.5, md: 2 },
                    bgcolor: message.sender === 'user' ? '#0077B8' : 'white',
                    color: message.sender === 'user' ? 'white' : '#1e293b',
                    borderRadius: message.sender === 'user' 
                      ? '18px 18px 4px 18px' 
                      : '18px 18px 18px 4px',
                    border: message.sender === 'bot' ? '1px solid #e2e8f0' : 'none',
                    boxShadow: message.sender === 'user' 
                      ? '0 2px 8px rgba(0, 119, 184, 0.15)'
                      : '0 2px 8px rgba(0, 0, 0, 0.06)',
                    ...(message.isError && {
                      bgcolor: '#fee2e2',
                      color: '#dc2626',
                      border: '1px solid #fca5a5'
                    })
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.7,
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      letterSpacing: '0.01em',
                    }}
                  >
                    {message.text}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      opacity: 0.7,
                      fontSize: '0.65rem',
                    }}
                  >
                    {message.timestamp.toLocaleTimeString('fr-FR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </Typography>
                </Paper>
                {message.sender === 'user' && (
                  <Avatar 
                    sx={{ 
                      bgcolor: '#F5A000',
                      width: 32,
                      height: 32,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                )}
              </Box>
          ))}
          
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.75 }}>
              <Avatar sx={{ bgcolor: '#0077B8', width: 32, height: 32 }}>
                <LightbulbIcon sx={{ fontSize: 18, color: '#F5A000' }} />
              </Avatar>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 1.5,
                  bgcolor: 'white',
                  borderRadius: '16px 16px 16px 2px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} thickness={4} sx={{ color: '#0077B8' }} />
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                    Réflexion...
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Zone de saisie */}
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            bgcolor: 'white',
            borderTop: '1px solid #e2e8f0',
            boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Posez votre question..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading || connectionError}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '24px',
                  bgcolor: '#f8fafc',
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  padding: { xs: '8px 14px', md: '10px 16px' },
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0077B8',
                    borderWidth: '2px',
                  },
                  '& textarea': {
                    lineHeight: 1.6,
                  }
                }
              }}
            />
            <IconButton
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || isLoading || connectionError}
              sx={{
                bgcolor: '#0077B8',
                color: 'white',
                width: { xs: 44, md: 48 },
                height: { xs: 44, md: 48 },
                '&:hover': {
                  bgcolor: '#00ACEA',
                  transform: 'scale(1.05)',
                },
                '&:disabled': {
                  bgcolor: '#e2e8f0',
                  color: '#94a3b8',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <SendIcon sx={{ fontSize: { xs: 20, md: 22 } }} />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default Chatbot;
